/**
 * Human-readable call-flow summary for routing rule list cards/rows.
 *
 * Produces a compact, one-line summary of the form:
 *   "From <source> → <called pattern>[; calling <pattern>] → To <destination>"
 *
 * Source and destination peer/group UUIDs are resolved to display names using
 * the peers/peer-groups already fetched by the page via React Query hooks.
 * The helpers are pure and unit-testable.
 */

import type { Sip } from '@sbclite/core';

/** Minimal shape needed to resolve a peer or peer-group name from its UUID. */
export interface FlowSummaryEntity {
  id: string;
  name: string;
}

/** Human-readable verb for each structured number-pattern type. */
const PATTERN_TYPE_VERB: Record<string, string> = {
  prefix: 'starts with',
  suffix: 'ends with',
  exact: 'is',
  contains: 'contains',
  regex: 'matches',
  any: 'any',
};

function patternVerb(type: string): string {
  return PATTERN_TYPE_VERB[type] ?? 'matches';
}

/** Resolve the "From" side of a rule to a human-readable source name. */
export function resolveSourceName(
  rule: Sip.RoutingRuleInfo,
  peers: FlowSummaryEntity[],
  peerGroups: FlowSummaryEntity[],
): string {
  const sourceScope = rule.sourceScope ?? (rule.sourcePeerId ? 'sip_peer' : rule.sourcePeerGroupId ? 'peer_group' : 'any');

  if (sourceScope === 'sip_peer') {
    if (rule.sourcePeerId) {
      const peer = peers.find((p) => p.id === rule.sourcePeerId);
      return peer ? peer.name : 'Unknown peer';
    }
    return 'Unknown peer';
  }

  if (sourceScope === 'peer_group') {
    if (rule.sourcePeerGroupId) {
      const group = peerGroups.find((g) => g.id === rule.sourcePeerGroupId);
      return group ? group.name : 'Unknown group';
    }
    return 'Unknown group';
  }

  return 'Any';
}

/** Summarize the called-number (To/Request-URI) pattern, e.g. "called contains 468280000". */
export function summarizeCalledPattern(rule: Sip.RoutingRuleInfo): string {
  const called = rule.numberPattern?.called;
  if (called && called.type !== 'any' && called.value) {
    return `called ${patternVerb(called.type)} ${called.value}`;
  }
  if (rule.conditions?.toPattern) {
    return `called matches ${rule.conditions.toPattern}`;
  }
  return 'any number';
}

/**
 * Summarize the calling-number (From) pattern, e.g. "calling starts with 1".
 * Returns an empty string when there is no calling constraint so it can be
 * omitted from the summary.
 */
export function summarizeCallingPattern(rule: Sip.RoutingRuleInfo): string {
  const calling = rule.numberPattern?.calling;
  if (calling && calling.type !== 'any' && calling.value) {
    return `calling ${patternVerb(calling.type)} ${calling.value}`;
  }
  if (rule.conditions?.fromPattern) {
    return `calling matches ${rule.conditions.fromPattern}`;
  }
  return '';
}

/** Resolve the "To" side of a rule to a human-readable destination name. */
export function resolveDestinationName(
  rule: Sip.RoutingRuleInfo,
  peers: FlowSummaryEntity[],
  peerGroups: FlowSummaryEntity[],
): string {
  const dt = rule.destinationType;
  if (dt === 'reject') return 'Reject';
  if (dt === 'peer' || dt === 'sip_peer') {
    if (!rule.destinationTarget) return '-';
    const peer = peers.find((p) => p.id === rule.destinationTarget);
    return peer ? peer.name : rule.destinationTarget;
  }
  if (dt === 'peerGroup' || dt === 'peer_group') {
    if (!rule.destinationTarget) return '-';
    const group = peerGroups.find((g) => g.id === rule.destinationTarget);
    return group ? group.name : rule.destinationTarget;
  }
  // Unsupported destination type — surface the raw type (and target if any)
  // so the list never hides what the rule actually does.
  if (rule.destinationTarget) return `${dt} → ${rule.destinationTarget}`;
  return dt;
}

/**
 * Build a compact, one-line call-flow summary for a routing rule.
 *
 * @example
 * "From Carrier A → called starts with +1 → To Internal PBX"
 * "From Any → any number; calling starts with 1 → To Reject"
 */
export function formatFlowSummary(
  rule: Sip.RoutingRuleInfo,
  peers: FlowSummaryEntity[],
  peerGroups: FlowSummaryEntity[],
): string {
  const from = resolveSourceName(rule, peers, peerGroups);
  const called = summarizeCalledPattern(rule);
  const calling = summarizeCallingPattern(rule);
  const to = resolveDestinationName(rule, peers, peerGroups);
  const middle = calling ? `${called}; ${calling}` : called;
  return `From ${from} → ${middle} → To ${to}`;
}
